/**
 * Gifting and shopping occasions in the GCC and India, for planning campaigns ahead of time. Islamic occasions
 * follow the Umm al-Qura calendar (the date can shift by a day once the moon is sighted, so campaigns start early).
 */
export const OCCASIONS = [
  'ramadan',
  'eid_al_fitr',
  'eid_al_adha',
  'national_day',
  'founding_day',
  'white_friday',
  'mothers_day',
  'valentines',
  'new_year',
] as const;
export type Occasion = (typeof OCCASIONS)[number];

/** How many days before the occasion its campaign should start. */
export const LEAD_DAYS: Record<Occasion, number> = {
  ramadan: 14,
  eid_al_fitr: 10,
  eid_al_adha: 10,
  national_day: 14,
  founding_day: 10,
  white_friday: 7,
  mothers_day: 10,
  valentines: 7,
  new_year: 7,
};

const NATIONAL_DAY: Record<string, string> = {
  QA: '12-18',
  AE: '12-02',
  SA: '09-23',
  KW: '02-25',
  BH: '12-16',
  OM: '11-20',
  IN: '08-15',
};
const FIXED: Partial<Record<Occasion, string>> = { valentines: '02-14', new_year: '01-01' };
const HIJRI: Partial<Record<Occasion, [month: number, day: number]>> = {
  ramadan: [9, 1],
  eid_al_fitr: [10, 1],
  eid_al_adha: [12, 10],
};

const hijriFmt = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
  timeZone: 'Asia/Riyadh',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});
function hijri(d: Date) {
  const p = Object.fromEntries(hijriFmt.formatToParts(d).map((x) => [x.type, x.value]));
  return { month: Number(p.month), day: Number(p.day) };
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const utcDay = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d, 12));

/** The day after the fourth Thursday of November ("White Friday" in the Gulf). */
function whiteFriday(year: number): Date {
  const first = utcDay(year, 11, 1);
  const thursday = 1 + ((4 - first.getUTCDay() + 7) % 7);
  return utcDay(year, 11, thursday + 21 + 1);
}

export interface UpcomingOccasion {
  key: Occasion;
  /** The occasion's date (YYYY-MM-DD). */
  date: string;
  /** When its campaign should start. */
  startCampaignBy: string;
  daysAway: number;
}

/** Occasions on or after `from` within `days`, soonest first. */
export function upcomingOccasions(country: string, from: Date, days = 120): UpcomingOccasion[] {
  const start = utcDay(from.getUTCFullYear(), from.getUTCMonth() + 1, from.getUTCDate());
  const end = new Date(start.getTime() + days * 86_400_000);
  const found: { key: Occasion; date: Date }[] = [];
  const years = [start.getUTCFullYear(), start.getUTCFullYear() + 1];
  const md = (key: Occasion, v: string | undefined) => {
    if (!v) return;
    const [m, d] = v.split('-').map(Number) as [number, number];
    for (const y of years) found.push({ key, date: utcDay(y, m, d) });
  };
  md('national_day', NATIONAL_DAY[country]);
  if (country === 'SA') md('founding_day', '02-22');
  // Mother's Day is 21 March across the Arab world; India marks it in May (not listed).
  if (country !== 'IN') md('mothers_day', '03-21');
  for (const [k, v] of Object.entries(FIXED)) md(k as Occasion, v);
  for (const y of years) found.push({ key: 'white_friday', date: whiteFriday(y) });
  for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
    const d = new Date(t);
    const h = hijri(d);
    for (const [k, [m, day]] of Object.entries(HIJRI) as [Occasion, [number, number]][])
      if (h.month === m && h.day === day) found.push({ key: k, date: d });
  }
  return found
    .filter((o) => o.date >= start && o.date <= end)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((o) => ({
      key: o.key,
      date: iso(o.date),
      startCampaignBy: iso(new Date(o.date.getTime() - LEAD_DAYS[o.key] * 86_400_000)),
      daysAway: Math.round((o.date.getTime() - start.getTime()) / 86_400_000),
    }));
}
